"use client";
import { TODOS } from "@/lib/idiomas";
import React, { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import Cookies from "js-cookie";
import { usePaisDetectado } from "@/lib/geo/pais";
import api from "@/lib/api";
import { resolvePacks } from "@/lib/packs";
import axios from "axios";
import { usePathname, useRouter } from "next/navigation";
import { useSearchParamsDelVisitante } from "@/lib/useSearchParamsDelVisitante";
import { useTranslation } from "react-i18next";
import { safeBack } from "@/components/BackButton";
import { trackViewItemList, trackAddToCart, trackAddToCartPack } from "@/lib/analytics";
import { isPreorderMeta, PREORDER_LANGS_BY_SLUG } from "@/lib/books/preorder";

const normalizeCC = (value) => {
  const cc = String(value || "").trim().slice(0, 2).toUpperCase();
  return /^[A-Z]{2}$/.test(cc) ? cc : "";
};

const EUROPE_ISO_SET = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR",
  "HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK",
  "SI","ES","SE","IS","LI","NO","CH","GB","AL","RS","UA","MD",
  "BY","GE","AM","AZ","AD","MC","SM","VA","ME","BA","MK","XK",
]);

const PRODUCT_IDS = {
  ARMONIA: 1, MODOS: 11, ARMONIA2: 21, TENSIONES: 31, GLUE: 41, ARREGLOS: 51, ESCALAS: 61, MIC: 71,
};

// Esta pantalla es el "catalogo completo", asi que lista TODOS los libros
// vendibles, incluidos los que todavia estan en preventa. Musica Ilustrada de
// Cero entra aca aunque no se entregue hasta noviembre: la card avisa por su
// subtitulo, que en los siete idiomas dice "preventa" y la fecha.
const ALL_PRODUCT_IDS = [
  PRODUCT_IDS.ARMONIA, PRODUCT_IDS.MODOS, PRODUCT_IDS.TENSIONES,
  PRODUCT_IDS.ARMONIA2, PRODUCT_IDS.GLUE, PRODUCT_IDS.ARREGLOS, PRODUCT_IDS.ESCALAS,
  PRODUCT_IDS.MIC,
];

const BOOKS_DATA = {
  [PRODUCT_IDS.ARMONIA]: {
    titleKey: "illustrated_harmony_title",
    subtitleKey: "armonia_ilustrada_subtitle",
    descriptionShortKey: "armonia_ilustrada.description_short",
    accentColor: "#d0ac22",
    altName: "Armonía Ilustrada",
    slug: "ai",
    analyticsKey: "armonia",
    coverByLang: {
      es: "/book/es/tapa_armonia_ilustrada.jpg",
      en: "/book/en/tapa_armonia_ilustrada.jpg",
      pt: "/book/pt/tapa_armonia_ilustrada.jpg",
      fr: "/book/fr/tapa_armonia_ilustrada.jpg",
      it: "/book/it/tapa_armonia_ilustrada.jpg",
      de: "/book/de/tapa_armonia_ilustrada.jpg",
      ja: "/book/ja/tapa_armonia_ilustrada.jpg",
      tr: "/book/tr/tapa_armonia_ilustrada.jpg",
    },
  },
  [PRODUCT_IDS.MODOS]: {
    titleKey: "illustrated_modes_title",
    subtitleKey: "modos_ilustrados_subtitle",
    descriptionShortKey: "modos_ilustrados.description_short",
    accentColor: "#e588b5",
    altName: "Modos Ilustrados",
    slug: "mi",
    analyticsKey: "modos",
    coverByLang: {
      es: "/book/es/tapa_modos_ilustrados.jpg",
      en: "/book/en/tapa_modos_ilustrados.jpg",
      pt: "/book/pt/tapa_modos_ilustrados.jpg",
      fr: "/book/fr/tapa_modos_ilustrados.jpg",
      it: "/book/it/tapa_modos_ilustrados.jpg",
      de: "/book/de/tapa_modos_ilustrados.jpg",
      ja: "/book/ja/tapa_modos_ilustrados.jpg",
      tr: "/book/tr/tapa_modos_ilustrados.jpg",
    },
  },
  [PRODUCT_IDS.TENSIONES]: {
    titleKey: "illustrated_tensions_title",
    subtitleKey: "tensiones_ilustradas_subtitle",
    descriptionShortKey: "tensiones_ilustradas.description_short",
    accentColor: "#81d0e1",
    altName: "Tensiones Ilustradas",
    slug: "ti",
    analyticsKey: "tensiones",
    coverByLang: {
      es: "/book/es/tapa_tensiones_ilustradas.jpg",
      en: "/book/en/tapa_tensiones_ilustradas.jpg",
      pt: "/book/pt/tapa_tensiones_ilustradas.jpg",
      fr: "/book/fr/tapa_tensiones_ilustradas.jpg",
      it: "/book/it/tapa_tensiones_ilustradas.jpg",
      de: "/book/de/tapa_tensiones_ilustradas.jpg",
      ja: "/book/ja/tapa_tensiones_ilustradas.jpg",
      tr: "/book/tr/tapa_tensiones_ilustradas.jpg",
    },
  },
  [PRODUCT_IDS.ARMONIA2]: {
    titleKey: "illustrated_harmony_2_title",
    subtitleKey: "armonia_ilustrada_2_subtitle",
    descriptionShortKey: "armonia_ilustrada_2.description_short",
    accentColor: "#95ca44",
    altName: "Armonía Ilustrada 2",
    slug: "ai2",
    analyticsKey: "armonia2",
    coverByLang: {
      es: "/book/es/tapa_armonia_ilustrada_2.jpg",
      en: "/book/en/tapa_armonia_ilustrada_2.jpg",
      pt: "/book/pt/tapa_armonia_ilustrada_2.jpg",
      fr: "/book/fr/tapa_armonia_ilustrada_2.jpg",
      it: "/book/it/tapa_armonia_ilustrada_2.jpg",
      de: "/book/de/tapa_armonia_ilustrada_2.jpg",
      ja: "/book/ja/tapa_armonia_ilustrada_2.jpg",
      tr: "/book/tr/tapa_armonia_ilustrada_2.jpg",
    },
  },
  [PRODUCT_IDS.GLUE]: {
    titleKey: "magic_glue_title",
    subtitleKey: "glue_magico_subtitle",
    descriptionShortKey: "glue_magico.description_short",
    accentColor: "#c45646",
    altName: "Glue Mágico para Guitarristas",
    slug: "glue",
    analyticsKey: "glue",
    coverByLang: {
      es: "/book/es/tapa_glue_magico_para_guitarristas.jpg",
      en: "/book/en/tapa_glue_magico_para_guitarristas.jpg",
      pt: "/book/pt/tapa_glue_magico_para_guitarristas.jpg",
      fr: "/book/fr/tapa_glue_magico_para_guitarristas.jpg",
      it: "/book/it/tapa_glue_magico_para_guitarristas.jpg",
      de: "/book/de/tapa_glue_magico_para_guitarristas.jpg",
      ja: "/book/ja/tapa_glue_magico_para_guitarristas.jpg",
      tr: "/book/tr/tapa_glue_magico_para_guitarristas.jpg",
    },
  },
  [PRODUCT_IDS.ARREGLOS]: {
    titleKey: "illustrated_arrangements_title",
    subtitleKey: "arreglos_ilustrados_subtitle",
    descriptionShortKey: "arreglos_ilustrados.description_short",
    accentColor: "#e29f53",
    altName: "Arreglos Ilustrados",
    slug: "arreglos",
    analyticsKey: "arreglos",
    coverByLang: {
      es: "/book/es/tapa_arreglos_ilustrados.jpg",
      en: "/book/en/tapa_arreglos_ilustrados.jpg",
      pt: "/book/pt/tapa_arreglos_ilustrados.jpg",
      fr: "/book/fr/tapa_arreglos_ilustrados.jpg",
      it: "/book/it/tapa_arreglos_ilustrados.jpg",
      de: "/book/de/tapa_arreglos_ilustrados.jpg",
      ja: "/book/ja/tapa_arreglos_ilustrados.jpg",
      tr: "/book/tr/tapa_arreglos_ilustrados.jpg",
    },
  },
  [PRODUCT_IDS.ESCALAS]: {
    titleKey: "illustrated_scales_title",
    subtitleKey: "escalas_ilustradas_subtitle",
    // Ya se entrega en los siete idiomas, así que el subtítulo es el de siempre,
    // "154 páginas + 1 app + Videos", y va en gris como el resto. El resaltado con
    // la fecha era de la preventa y ya no aplica a este libro.
    descriptionShortKey: "escalas_ilustradas.description_short",
    accentColor: "#9288ff",
    altName: "Escalas Ilustradas",
    slug: "ei",
    analyticsKey: "escalas",
    coverByLang: {
      es: "/book/es/tapa_escalas_ilustradas.png",
      en: "/book/en/tapa_escalas_ilustradas.png",
      pt: "/book/pt/tapa_escalas_ilustradas.png",
      fr: "/book/fr/tapa_escalas_ilustradas.png",
      it: "/book/it/tapa_escalas_ilustradas.png",
      de: "/book/de/tapa_escalas_ilustradas.png",
      ja: "/book/ja/tapa_escalas_ilustradas.png",
      tr: "/book/tr/tapa_escalas_ilustradas.png",
    },
  },
  [PRODUCT_IDS.MIC]: {
    titleKey: "illustrated_music_zero_title",
    // El subtitulo de este libro NO es "N paginas + N apps": todavia no se sabe
    // cuantas trae. Es la linea de preventa con la fecha, distinta por idioma
    // (30-nov el español, 10-dic el resto), y por eso preorderLangs esta abajo:
    // isPreorderMeta la pinta con el color del libro en vez de gris.
    subtitleKey: "musica_ilustrada_de_cero_subtitle",
    descriptionShortKey: "musica_ilustrada_de_cero.description_short",
    accentColor: "#e79b45",
    altName: "Música Ilustrada de Cero",
    slug: "mic",
    analyticsKey: "musicacero",
    preorderLangs: PREORDER_LANGS_BY_SLUG.mic,
    coverByLang: {
      es: "/book/es/tapa_musica_ilustrada_de_cero-v2.png",
      en: "/book/en/tapa_musica_ilustrada_de_cero-v2.png",
      pt: "/book/pt/tapa_musica_ilustrada_de_cero-v2.png",
      fr: "/book/fr/tapa_musica_ilustrada_de_cero-v2.png",
      it: "/book/it/tapa_musica_ilustrada_de_cero-v2.png",
      de: "/book/de/tapa_musica_ilustrada_de_cero-v2.png",
      ja: "/book/ja/tapa_musica_ilustrada_de_cero-v2.png",
      tr: "/book/tr/tapa_musica_ilustrada_de_cero-v2.png",
    },
  },
};

const PACK_CONFIG = {
  panorama: { productIds: [PRODUCT_IDS.ARMONIA, PRODUCT_IDS.MODOS, PRODUCT_IDS.TENSIONES], slug: "panorama", nameKey: "pack_panorama", subtitleKey: "pack_panorama_subtitle", descriptionKey: "pack_panorama_description", accentColor: "#475b7e" },
  futuro: { productIds: [PRODUCT_IDS.ARMONIA, PRODUCT_IDS.MODOS, PRODUCT_IDS.TENSIONES, PRODUCT_IDS.ARMONIA2, PRODUCT_IDS.ARREGLOS, PRODUCT_IDS.ESCALAS], slug: "futuro", nameKey: "pack_futuro", subtitleKey: "pack_futuro_subtitle", descriptionKey: "pack_futuro_description", accentColor: "#c45646" },
};

function PackCard({ pack, langFromPath, localePath, currency, fmtPrice, originalPrice, finalPrice, discountPct, hasPrice }) {
  const { t } = useTranslation("books");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const packUrl = localePath(`/pack/${pack.slug}`);
  const buyUrl = localePath(`/checkout-pay?products=${pack.productIds.join(",")}`);

  const onBuyClick = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    const packName = `Pack ${pack.slug.charAt(0).toUpperCase() + pack.slug.slice(1)}`;
    try {
      await trackAddToCartPack(pack.slug, packName, finalPrice, currency);
    } catch (err) {
      console.error("Error tracking pack add_to_cart", err);
    } finally {
      window.location.href = buyUrl;
    }
  };

  const covers = pack.productIds.map((pid) => {
    const book = BOOKS_DATA[pid];
    return book ? (book.coverByLang[langFromPath] || book.coverByLang.es) : null;
  }).filter(Boolean);

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: "20px 0",
        borderBottom: "1px solid #eee",
        alignItems: "flex-start",
      }}
    >
      {/* Covers stack */}
      <a href={packUrl} style={{ flexShrink: 0, width: 100, position: "relative", height: 60 }}>
        {covers.map((src, idx) => {
          const book = BOOKS_DATA[pack.productIds[idx]];
          return (
            <div
              key={idx}
              style={{
                position: "absolute",
                top: idx * 4,
                left: idx * 12,
                width: 65,
                padding: 2,
                background: book?.accentColor || "#ccc",
                borderRadius: 4,
              }}
            >
              <Image
                width={120}
                height={85}
                quality={40}
                style={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: "1000/707",
                  objectFit: "cover",
                  borderRadius: 2,
                  display: "block",
                }}
                src={src}
                alt={`Pack ${t(pack.nameKey)}`}
              />
            </div>
          );
        })}
      </a>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            {pack.productIds.map((pid) => (
              <div key={pid} style={{ width: 4, height: 16, background: BOOKS_DATA[pid]?.accentColor || "#ccc", borderRadius: 2 }} />
            ))}
          </div>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "#111", margin: 0, lineHeight: 1.3 }}>
            {t("pack")} {t(pack.nameKey)}
          </h2>
        </div>
        <p style={{ color: "#999", fontSize: 12, margin: "2px 0 6px" }}>
          {t(pack.subtitleKey)}
        </p>
        <p style={{ fontSize: 13, color: "#666", margin: "0 0 10px", lineHeight: 1.5 }}>
          {t(pack.descriptionKey).replace(/<[^>]+>/g, "")}
        </p>

        {/* Price + button rows */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div className="catalog-btn-row">
            <a
              href={packUrl}
              className="btn btn-mod btn-circle"
              style={{
                background: "#fff",
                border: `2px solid ${pack.accentColor}`,
                borderRadius: "999px",
                color: "#111",
                fontSize: 12,
                padding: "6px 14px",
                textDecoration: "none",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              data-btn-animate="y"
            >
              {t("l2_see_more")}
            </a>
            <button
              onClick={onBuyClick}
              disabled={isRedirecting}
              className="btn btn-mod btn-circle"
              style={{
                background: pack.accentColor,
                border: `2px solid ${pack.accentColor}`,
                borderRadius: "999px",
                color: "#fff",
                fontSize: 12,
                padding: "6px 14px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isRedirecting ? "wait" : "pointer",
              }}
              data-btn-animate="y"
            >
              {t("l2_buy")}
            </button>
          </div>

          {hasPrice && (
            <div style={{ textAlign: "right" }}>
              {discountPct > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <span style={{ background: "#111", color: "#fff", fontSize: 11, padding: "2px 6px", borderRadius: 50, fontWeight: 700 }}>
                    {discountPct}% OFF
                  </span>
                </div>
              )}
              {discountPct > 0 && originalPrice != null && (
                <div style={{ fontSize: 12, color: "#999", textDecoration: "line-through", lineHeight: 1.2 }}>
                  {currency} {fmtPrice(originalPrice)}
                </div>
              )}
              <div style={{ fontSize: 20, fontWeight: 700, color: "#111", lineHeight: 1.3 }}>
                {currency} {fmtPrice(finalPrice)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BookCard({ bookData, productId, langFromPath, localePath, currency, fmtPrice, originalPrice, finalPrice, discountPct, hasPrice }) {
  const { t } = useTranslation("books");
  const [isRedirecting, setIsRedirecting] = useState(false);
  const cover = bookData.coverByLang[langFromPath] || bookData.coverByLang.es;
  const isPrelaunch = isPreorderMeta(bookData, langFromPath);
  const bookUrl = localePath(`/libro/${bookData.slug}`);
  const buyUrl = localePath(`/checkout-pay?products=${productId}`);

  const onBuyClick = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    try {
      await trackAddToCart(bookData.analyticsKey, currency, finalPrice);
    } catch (err) {
      console.error("Error tracking add_to_cart", err);
    } finally {
      window.location.href = buyUrl;
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        padding: "20px 0",
        borderBottom: "1px solid #eee",
        alignItems: "flex-start",
      }}
    >
      {/* Cover */}
      <a href={bookUrl} style={{ flexShrink: 0, width: 100, padding: 2, background: bookData.accentColor, borderRadius: 6 }}>
        <Image
          width={200}
          height={141}
          quality={60}
          className="img-fluid"
          style={{
            aspectRatio: "1000/707",
            objectFit: "cover",
            borderRadius: 4,
            display: "block",
          }}
          src={cover}
          alt={`Tapa ${bookData.altName}`}
        />
      </a>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <div style={{ width: 4, height: 16, background: bookData.accentColor, borderRadius: 2, flexShrink: 0 }} />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "#111", margin: 0, lineHeight: 1.3 }}>
            {t(bookData.titleKey)}
          </h2>
        </div>
        <p style={{
          color: isPrelaunch ? bookData.accentColor : "#999",
          fontWeight: isPrelaunch ? 700 : 400,
          fontSize: 12,
          margin: "2px 0 6px",
        }}>
          {t(bookData.subtitleKey)}
        </p>
        <p style={{ fontSize: 13, color: "#666", margin: "0 0 10px", lineHeight: 1.5 }}>
          {t(bookData.descriptionShortKey)}
        </p>

        {/* Price + button rows */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div className="catalog-btn-row">
            <a
              href={bookUrl}
              className="btn btn-mod btn-circle"
              style={{
                background: "#fff",
                border: `2px solid ${bookData.accentColor}`,
                borderRadius: "999px",
                color: "#111",
                fontSize: 12,
                padding: "6px 14px",
                textDecoration: "none",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              data-btn-animate="y"
            >
              {t("l2_see_more")}
            </a>
            <button
              onClick={onBuyClick}
              disabled={isRedirecting}
              className="btn btn-mod btn-circle"
              style={{
                background: bookData.accentColor,
                border: `2px solid ${bookData.accentColor}`,
                borderRadius: "999px",
                color: "#fff",
                fontSize: 12,
                padding: "6px 14px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isRedirecting ? "wait" : "pointer",
              }}
              data-btn-animate="y"
            >
              {t(isPrelaunch ? "l2_buy_preorder" : "l2_buy")}
            </button>
          </div>

          {hasPrice && (
            <div style={{ textAlign: "right" }}>
              {discountPct > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <span style={{ background: "#111", color: "#fff", fontSize: 11, padding: "2px 6px", borderRadius: 50, fontWeight: 700 }}>
                    {discountPct}% OFF
                  </span>
                </div>
              )}
              {discountPct > 0 && originalPrice != null && (
                <div style={{ fontSize: 12, color: "#999", textDecoration: "line-through", lineHeight: 1.2 }}>
                  {currency} {fmtPrice(originalPrice)}
                </div>
              )}
              <div style={{ fontSize: 20, fontWeight: 700, color: "#111", lineHeight: 1.3 }}>
                {currency} {fmtPrice(finalPrice)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HomeLibro2Catalogo() {
  const { t, i18n } = useTranslation("books");
  const searchParams = useSearchParamsDelVisitante();
  const pathname = usePathname();
  const router = useRouter();

  const SUPPORTED = TODOS;
  const langFromPath = useMemo(() => {
    const seg = (pathname?.split("/")?.[1] || "").toLowerCase();
    if (SUPPORTED.includes(seg)) return seg;
    const i18nLang = (i18n.language || "").toLowerCase().split("-")[0];
    return SUPPORTED.includes(i18nLang) ? i18nLang : "es";
  }, [pathname, i18n.language]);

  const localePath = (path) => `/${langFromPath}${path}`;

  const [countryCode, setCountryCode] = usePaisDetectado();

  const [pricesMatrix, setPricesMatrix] = useState(null);
  const [priceMap, setPriceMap] = useState({});
  const [discounts, setDiscounts] = useState({});
  const [currency, setCurrency] = useState("USD");
  const [displayLocale, setDisplayLocale] = useState("es-AR");
  const [pricingCountry, setPricingCountry] = useState(null);
  const [packDiscounts, setPackDiscounts] = useState([]);
  const hasTrackedViewListRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedViewListRef.current && countryCode) {
      hasTrackedViewListRef.current = true;
      trackViewItemList(countryCode === "AR" ? "ARS" : "USD");
    }
  }, [countryCode]);

  useEffect(() => {
    const q = normalizeCC(searchParams.get("cc"));
    if (q) {
      setCountryCode((prev) => {
        const current = String(prev || "").toUpperCase();
        if (q !== current) {
          Cookies.set("cc", q, { expires: 30, sameSite: "Lax" });
          return q;
        }
        return prev;
      });
    }
  }, [searchParams]);

  useEffect(() => {
    const ac = new AbortController();
    api.get("/api/prices/matrix", { params: { product_ids: "1,11,21,31,41,51,61,71" }, signal: ac.signal })
      .then(async (pricesRes) => {
        // Packs primero, y recien ahi commiteamos los dos estados juntos: si los packs no
        // se pueden resolver, no seteamos precios y la UI muestra "—" en vez de un pack sin
        // descuento (que el back si cobraria con descuento). Fail-closed.
        const packs = await resolvePacks(pricesRes, { signal: ac.signal });
        setPricesMatrix(pricesRes?.data ?? null);
        setPackDiscounts(packs);
      })
      .catch((e) => { if (!axios.isCancel(e)) console.error("[catalogo] fetch error:", e); });
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (!countryCode || !pricesMatrix) return;
    try {
      const rawCountries = pricesMatrix?.countries ?? [];
      const products = pricesMatrix?.products ?? [];
      const prices = pricesMatrix?.prices ?? [];
      const countries = rawCountries.map((c) => ({
        ...c,
        code: String(c.code || "").toUpperCase(),
        currencyCode: String(c.currencyCode || "").toUpperCase(),
        originalRoundStep: c.originalRoundStep ?? c.original_round_step ?? null,
        packRoundStep: c.packRoundStep ?? c.pack_round_step ?? null,
        displayLocale: c.displayLocale ?? c.display_locale ?? null,
      }));
      if (!countries.length) {
        setPriceMap({}); setDiscounts({}); setCurrency("USD");
        return;
      }
      const byCode = {};
      for (const c of countries) byCode[c.code] = c;
      const cc = String(countryCode).toUpperCase();
      let base = byCode[cc] || null;
      if (!base && EUROPE_ISO_SET.has(cc)) base = byCode["EU"] || null;
      if (!base) base = byCode["RW"] || null;
      if (!base) base = [...countries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))[0];
      setPricingCountry(base);

      const discMap = {};
      for (const p of products) {
        const d = Number(p?.discount);
        discMap[p.id] = Number.isFinite(d) ? d : 0;
      }
      setDiscounts(discMap);

      const byProduct = {};
      for (const row of prices) {
        if (Number(row.countryId) === Number(base.id)) {
          byProduct[row.productId] = row.price == null ? null : Number(row.price);
        }
      }
      setPriceMap(byProduct);

      setDisplayLocale(base.displayLocale || (cc === "AR" ? "es-AR" : "en-US"));
      setCurrency(base.currencyCode || "USD");
    } catch (e) {
      console.error("[catalogo] price processing error:", e);
      setPriceMap({}); setDiscounts({}); setCurrency("USD");
    }
  }, [countryCode, pricesMatrix]);

  const currencyExponent = (curr) => {
    const C = String(curr || "USD").toUpperCase();
    if (["JPY","KRW","VND","CLP","ISK","UGX","PYG","RWF","GNF","XOF","XAF"].includes(C)) return 0;
    if (["BHD","JOD","KWD","OMR","TND","LYD"].includes(C)) return 3;
    return 2;
  };

  const fmtPrice = (v) => {
    const value = Number(v);
    return typeof value === "number" && !Number.isNaN(value)
      ? new Intl.NumberFormat(displayLocale, {
          minimumFractionDigits: currencyExponent(currency),
          maximumFractionDigits: currencyExponent(currency),
        }).format(value)
      : "—";
  };

  const roundToStep = (value, step) => {
    const v = Number(value);
    const s = Number(step);
    if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return value;
    const decimalsIn = (n) => { const str = String(n); const i = str.indexOf("."); return i === -1 ? 0 : str.length - i - 1; };
    const scale = Math.pow(10, decimalsIn(s));
    const V = Math.round(v * scale);
    const S = Math.round(s * scale);
    return Math.round(V / S) * S / scale;
  };

  const matchPackDiscount = (productIds) => {
    const idSet = new Set(productIds);
    let best = null;
    for (const pack of packDiscounts) {
      const pids = pack.product_ids || [];
      if (pids.length > 0 && pids.every((id) => idSet.has(id))) {
        if (!best || pack.discount_pct > best.discount_pct) best = pack;
      }
    }
    return best;
  };

  const computeOriginalFromDiscount = (price, discountPct) => {
    const d = Number(discountPct);
    if (Number.isFinite(price) && price > 0 && Number.isFinite(d) && d > 0 && d < 100) {
      const raw = price / (1 - d / 100);
      const step = Number(pricingCountry?.originalRoundStep);
      return Number.isFinite(step) && step > 0 ? roundToStep(raw, step) : raw;
    }
    return null;
  };

  const roundPackPrice = (value, step, originalTotal, targetDiscount) => {
    const v = Number(value); const s = Number(step);
    if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return value;
    const decimalsIn = (n) => { const str = String(n); const i = str.indexOf("."); return i === -1 ? 0 : str.length - i - 1; };
    const precision = decimalsIn(s);
    const ceiled  = parseFloat((Math.ceil(v / s) * s).toFixed(precision));
    const floored = parseFloat((Math.floor(v / s) * s).toFixed(precision));
    if (targetDiscount != null && Number.isFinite(originalTotal) && originalTotal > 0) {
      const ceilDiscPct = (1 - ceiled / originalTotal) * 100;
      if (ceilDiscPct < targetDiscount) return floored;
    }
    return ceiled;
  };

  const getPackPrice = (pack) => {
    let totalOriginal = 0;
    let allAvailable = true;
    for (const id of pack.productIds) {
      const price = priceMap[id];
      const discount = discounts[id] || 0;
      if (price == null) { allAvailable = false; continue; }
      const original = computeOriginalFromDiscount(price, discount);
      totalOriginal += original != null ? original : price;
    }
    const matched = matchPackDiscount(pack.productIds);
    const packPct = matched?.discount_pct || 0;
    const packPids = new Set(matched?.product_ids || []);
    let finalPrice = 0;
    for (const id of pack.productIds) {
      const price = priceMap[id];
      if (price == null) continue;
      finalPrice += packPids.has(id) && packPct > 0 ? price * (1 - packPct / 100) : price;
    }
    const packStep = Number(pricingCountry?.packRoundStep);
    if (Number.isFinite(packStep) && packStep > 0) {
      finalPrice = roundPackPrice(finalPrice, packStep, totalOriginal, matched?.target_discount);
    }
    const discountPct = totalOriginal > 0 ? Math.round((1 - finalPrice / totalOriginal) * 100) : 0;
    return { original: totalOriginal, final: finalPrice, discountPct, hasPrice: allAvailable && finalPrice > 0 };
  };

  const getBookPrice = (productId) => {
    const price = priceMap[productId];
    if (price == null) return { price: null, original: null, discountPct: 0, hasPrice: false };
    const discount = discounts[productId] || 0;
    let original = null;
    if (Number.isFinite(price) && price > 0 && Number.isFinite(discount) && discount > 0 && discount < 100) {
      const raw = price / (1 - discount / 100);
      const step = Number(pricingCountry?.originalRoundStep);
      original = Number.isFinite(step) && step > 0 ? roundToStep(raw, step) : raw;
    }
    const discountPct = (original != null && original > price) ? Math.round((1 - price / original) * 100) : 0;
    return { price, original, discountPct, hasPrice: true };
  };

  return (
    <>
      <style>{`
        .catalog-btn-row { display: flex; flex-direction: column; gap: 8px; }
        @media (min-width: 600px) { .catalog-btn-row { flex-direction: row; } }
      `}</style>
      {/* Header */}
      <section style={{ background: "#f8f8f8", padding: "120px 20px 20px" }}>
        <div style={{ maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
          <h1 style={{ fontSize: 32, fontWeight: 700, color: "#111", marginBottom: 8 }}>
            {t("l2_catalog_title")}
          </h1>
          <button
            onClick={() => safeBack(router, localePath("/libros"))}
            style={{ fontSize: 14, color: "#999", textDecoration: "underline", textUnderlineOffset: 3, background: "none", border: "none", cursor: "pointer" }}
          >
            {t("l2_back_to_options")}
          </button>
        </div>
      </section>

      {/* Book list */}
      <section style={{ background: "#fff", padding: "10px 20px 40px" }}>
        <div style={{ maxWidth: 500, margin: "0 auto" }}>
          {ALL_PRODUCT_IDS.map((productId) => {
            const bookData = BOOKS_DATA[productId];
            if (!bookData) return null;
            const pricing = getBookPrice(productId);
            return (
              <BookCard
                key={productId}
                bookData={bookData}
                productId={productId}
                langFromPath={langFromPath}
                localePath={localePath}
                currency={currency}
                fmtPrice={fmtPrice}
                originalPrice={pricing.original}
                finalPrice={pricing.price}
                discountPct={pricing.discountPct}
                hasPrice={pricing.hasPrice}
              />
            );
          })}
        </div>
      </section>

      {/* Packs */}
      <section style={{ background: "#fff", padding: "0 20px 40px" }}>
        <div style={{ maxWidth: 500, margin: "0 auto" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111", margin: "10px 0 0" }}>{t("packs")}</h2>
          {["panorama", "futuro"].map((key) => {
            const pack = PACK_CONFIG[key];
            const pricing = getPackPrice(pack);
            return (
              <PackCard
                key={key}
                pack={pack}
                langFromPath={langFromPath}
                localePath={localePath}
                currency={currency}
                fmtPrice={fmtPrice}
                originalPrice={pricing.original}
                finalPrice={pricing.final}
                discountPct={pricing.discountPct}
                hasPrice={pricing.hasPrice}
              />
            );
          })}
        </div>
      </section>

      {/* Back link */}
      <section style={{ background: "#fff", padding: "0 20px 60px", textAlign: "center" }}>
        <button
          onClick={() => safeBack(router, localePath("/libros"))}
          style={{ color: "#999", fontSize: 14, textDecoration: "underline", textUnderlineOffset: 3, background: "none", border: "none", cursor: "pointer" }}
        >
          {t("l2_back_to_options")}
        </button>
      </section>
    </>
  );
}
